import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell,
  LineChart, Line, ReferenceLine,
} from 'recharts';
import { PageShell } from '../components/Layout';
import { useApp } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';
import { useFlowClient } from '../hooks/useFlowClient';
import { formatDate } from '../utils/dateUtils';

/* ─── Enriched activity type ─── */

interface DashActivity {
  id: string;
  project_id: string;
  activity_id: string;
  activity_name: string;
  planned_start?: string | null;
  planned_finish?: string | null;
  actual_start?: string | null;
  actual_finish?: string | null;
  start_date?: string | null;
  finish_date?: string | null;
  total_float: number;
  float_days: number;        // Number()-normalized alias of total_float
  pct_complete: number;
  activity_status?: string;
  is_critical: boolean;      // from backend SCHEDULE.is_critical (worst-float / longest-path definition)
  early_start?: string | null;   // aliased from start_date
  early_finish?: string | null;  // aliased from finish_date
  wbs?: string;
  resource?: string;
}

/* ─── Card style shared constant ─── */

const card: React.CSSProperties = {
  background: 'white',
  border: '0.5px solid var(--border)',
  borderRadius: '12px',
};

/* ─── Dashboard ─── */

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { project_id } = useParams<{ project_id: string }>();
  const { projects, setSelectedProject } = useApp();
  const { userEmail } = useAuth();
  const flowClient = useFlowClient();

  const [activities, setActivities] = useState<DashActivity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const project = projects.find(p => p.id === project_id);

  useEffect(() => {
    if (project) setSelectedProject(project);
  }, [project, setSelectedProject]);

  // Scroll to a same-page anchor (e.g. Weekly Lookahead's batch-save popup linking to
  // "#most-critical-activities") once activities have loaded and the section actually
  // exists to scroll to — SPA navigation doesn't get the browser's native hash scroll.
  useEffect(() => {
    if (loading || !window.location.hash) return;
    const id = window.location.hash.slice(1);
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [loading]);

  useEffect(() => {
    if (!project_id || !userEmail) return;
    setLoading(true);
    setError(null);
    flowClient.ask(userEmail, project_id, 'get_project_activities')
      .then(res => {
        const parsed = JSON.parse(res.answer ?? '{}');
        const raw: any[] = parsed.activities ?? [];
        // Enrich: normalize float to number, derive is_critical and date aliases
        const enriched: DashActivity[] = raw.map(a => ({
          ...a,
          float_days: Number(a.total_float ?? a.float_days ?? 0),
          is_critical: a.is_critical === true,
          early_start: a.start_date ?? a.planned_start ?? null,
          early_finish: a.finish_date ?? a.planned_finish ?? null,
        }));
        setActivities(enriched);
      })
      .catch(err => setError(err?.message ?? 'Failed to load activities'))
      .finally(() => setLoading(false));
  }, [project_id, userEmail]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!project) {
    return (
      <PageShell className="py-20">
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>
          <p style={{ fontSize: '16px', fontWeight: 500, marginBottom: '8px', color: 'var(--text)' }}>Project not found</p>
          <p style={{ fontSize: '13px', color: 'var(--muted)', marginBottom: '16px' }}>This project may have been removed or you may not have access.</p>
          <button
            onClick={() => navigate(-1)}
            style={{ background: 'var(--accent)', color: 'white', padding: '10px 20px', borderRadius: '8px', border: 'none', fontSize: '14px', fontWeight: 500, cursor: 'pointer' }}
          >
            Go back
          </button>
        </div>
      </PageShell>
    );
  }

  /* ── Derived metrics ── */
  const totalCount = activities.length;
  const criticalCount = activities.filter(a => a.is_critical).length;
  const atRiskCount = activities.filter(a => Number(a.float_days) > 0 && Number(a.float_days) < 30).length;
  const completedCount = activities.filter(a => {
    const s = (a.activity_status ?? '').toLowerCase();
    return s === 'completed' || s === 'complete';
  }).length;
  const worstFloat = totalCount > 0
    ? Math.min(...activities.map(a => Number(a.float_days)))
    : project.float_days_remaining;

  const isDistressed = project.distressed_mode === 'Yes';
  const lastUploadDate = formatDate(project.last_upload_date);
  const cpmCompletionDate = (project as any).cpm_completion_date;
  const completionDate = cpmCompletionDate ? formatDate(cpmCompletionDate) : (project.target_completion ? formatDate(project.target_completion) : null);
  // Timezone-safe badge date: parseDateLocal strips the time component so UTC midnight doesn't shift to the previous day in US timezones.
  const completionBadgeDate = (() => {
    if (!cpmCompletionDate) return null;
    const [y, m, d] = String(cpmCompletionDate).substring(0, 10).split('-').map(Number);
    return new Date(y, m - 1, d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  })();
  const dataDateBadge = (() => {
    if (!project.data_date) return null;
    const [y, m, d] = String(project.data_date).substring(0, 10).split('-').map(Number);
    return new Date(y, m - 1, d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  })();

  /* ── Float distribution — 5 buckets with Number() coercion ── */
  console.log('[Dashboard] Float bucket debug:', {
    total: activities.length,
    sample: activities.slice(0, 3).map(a => ({ id: a.activity_id, float: a.float_days, type: typeof a.float_days })),
    critical: activities.filter(a => Number(a.float_days) <= 0).length,
    low: activities.filter(a => { const f = Number(a.float_days); return f > 0 && f < 14; }).length,
    medium: activities.filter(a => { const f = Number(a.float_days); return f >= 14 && f < 30; }).length,
    high: activities.filter(a => { const f = Number(a.float_days); return f >= 30 && f < 60; }).length,
    healthy: activities.filter(a => Number(a.float_days) >= 60).length,
  });
  // Float buckets: critical → watch → healthy → long float, all distinct, all from the tokens.
  const floatData = [
    { name: '≤0d',   value: activities.filter(a => Number(a.float_days) <= 0).length,                                            fill: 'var(--crit-text)' },
    { name: '1–13d', value: activities.filter(a => { const f = Number(a.float_days); return f > 0 && f < 14; }).length,          fill: 'var(--watch-text)' },
    { name: '14–29d',value: activities.filter(a => { const f = Number(a.float_days); return f >= 14 && f < 30; }).length,        fill: 'var(--ok-text)' },
    { name: '31–60d',value: activities.filter(a => { const f = Number(a.float_days); return f >= 30 && f < 60; }).length,        fill: 'var(--accent)' },
    { name: '>60d',  value: activities.filter(a => Number(a.float_days) >= 60).length,                                           fill: 'var(--ink)' },
  ];

  /* ── In-progress count ── */
  const inProgressCount = activities.filter(a => {
    const s = (a.activity_status ?? '').toLowerCase();
    return s === 'in progress' || s === 'started';
  }).length;

  /* ── Float history trend ── */
  const floatHistory: Array<{ date: string; float: number; trigger: 'upload' | 'approve'; label: string; completion_date: string | null }> = (() => {
    try { return (project as any)?.float_history ? JSON.parse((project as any).float_history) : []; }
    catch { return []; }
  })();
  const trendData = floatHistory.map(entry => ({
    date: new Date(entry.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    float: entry.float,
    trigger: entry.trigger,
    label: entry.label,
  }));

  const TrendDot = (props: any) => {
    const { cx, cy, payload } = props;
    if (!cx || !cy) return null;
    return payload.trigger === 'upload'
      ? <circle cx={cx} cy={cy} r={4} fill="var(--crit-text)" stroke="var(--crit-text)" />
      : <circle cx={cx} cy={cy} r={3} fill="white" stroke="var(--crit-text)" strokeWidth={1.5} />;
  };

  const TrendTooltip = ({ active, payload }: any) => {
    if (!active || !payload?.length) return null;
    const d = payload[0].payload;
    return (
      <div style={{ background: 'white', border: '0.5px solid var(--border)', borderRadius: '8px', padding: '8px 10px', fontSize: 11 }}>
        <div style={{ fontWeight: 500, marginBottom: 2 }}>{d.date}</div>
        <div style={{ color: d.float < 0 ? 'var(--crit-text)' : 'var(--ok-text)', fontWeight: 500 }}>{d.float}d float</div>
        <div style={{ color: 'var(--muted)', fontSize: 10, marginTop: 2 }}>{d.label}</div>
        <div style={{ fontSize: 10, marginTop: 2, color: d.trigger === 'upload' ? 'var(--accent)' : 'var(--watch-text)' }}>
          {d.trigger === 'upload' ? '● XER upload' : '○ In-app change'}
        </div>
      </div>
    );
  };

  /* ── Critical path Gantt data ── */
  const criticalActivities = activities
    .filter(a => a.is_critical)
    .sort((a, b) => Number(a.float_days) - Number(b.float_days))
    .slice(0, 8);

  const ganttTimestamps = criticalActivities.flatMap(a => [
    a.early_start ? new Date(a.early_start).getTime() : NaN,
    a.early_finish ? new Date(a.early_finish).getTime() : NaN,
  ]).filter(t => !isNaN(t));

  const minTime = ganttTimestamps.length > 0 ? Math.min(...ganttTimestamps) : 0;
  const maxTime = ganttTimestamps.length > 0 ? Math.max(...ganttTimestamps) : 1;
  const totalTime = maxTime - minTime || 1;

  const ganttLeft = (dateStr?: string | null): number => {
    if (!dateStr) return 0;
    const t = new Date(dateStr).getTime();
    return isNaN(t) ? 0 : ((t - minTime) / totalTime) * 100;
  };
  const ganttWidth = (start?: string | null, finish?: string | null): number => {
    if (!start || !finish) return 8;
    const s = new Date(start).getTime();
    const f = new Date(finish).getTime();
    if (isNaN(s) || isNaN(f)) return 8;
    return Math.max(((f - s) / totalTime) * 100, 2);
  };

  /* ── Metrics array ── */
  const metrics = [
    { label: 'Project float',       value: `${worstFloat}d`, sub: worstFloat < 0 ? 'Behind schedule' : 'On schedule',                                     color: worstFloat < 0 ? 'var(--crit-text)' : 'var(--ok-text)' },
    { label: 'Critical activities', value: criticalCount,    sub: 'On longest path',                                                                        color: 'var(--crit-text)' },
    { label: 'At risk',             value: atRiskCount,      sub: 'Float < 30 days',                                                                        color: 'var(--watch-text)' },
    { label: 'In progress',         value: inProgressCount,  sub: 'Active activities',                                                                      color: 'var(--accent)' },
    { label: 'Completed',           value: completedCount,   sub: totalCount > 0 ? `${Math.round(completedCount / totalCount * 100)}% done` : '—',          color: 'var(--ok-text)' },
  ];

  /* ── Quick actions ── */
  const quickActions = [
    { emoji: '📅', title: 'Weekly lookahead', subtitle: 'Tasks this week',     onClick: () => navigate(`/project/${project.id}/lookahead`), color: 'var(--accent)' },
    { emoji: '💬', title: 'Ask a question',   subtitle: 'AI schedule insights', onClick: () => navigate(`/project/${project.id}/ask`),       color: 'var(--accent)' },
    { emoji: '📄', title: 'Generate report',  subtitle: 'Weekly status report', onClick: () => navigate(`/project/${project.id}/reports`),   color: 'var(--watch-text)' },
    { emoji: '⬆️', title: 'Upload schedule',  subtitle: `Last: ${lastUploadDate}`, onClick: () => navigate(`/project/${project.id}/upload`), color: 'var(--muted)'    },
  ];

  return (
    <PageShell className="pt-4 pb-20">

        {/* ── Project header ── (badges wrap under the name on phones) */}
        <div style={{ ...card, padding: '12px 16px', marginBottom: '12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' }}>
          <div>
            <h1 className="h1-page break-anywhere">{project.project_name}</h1>
            <div style={{ fontSize: '15px', color: 'var(--muted)', marginTop: '2px' }}>Last upload: {lastUploadDate}</div>
            <div className="hero-num" style={{ color: worstFloat < 0 ? 'var(--crit-text)' : 'var(--ok-text)', marginTop: '4px' }}>
              {worstFloat}d
            </div>
            <div style={{ fontSize: '11px', color: worstFloat < 0 ? 'var(--crit-text)' : 'var(--ok-text)' }}>
              {worstFloat < 0 ? 'Behind schedule' : 'On schedule'}
            </div>
          </div>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            {isDistressed && (
              <span style={{ background: 'var(--crit-bg)', color: 'var(--crit-text)', fontSize: '11px', fontWeight: 500, padding: '3px 10px', borderRadius: '20px' }}>
                ⚠ Distressed
              </span>
            )}
            {!loading && totalCount > 0 && (
              <span style={{ background: 'var(--tint)', color: 'var(--accent)', fontSize: '11px', fontWeight: 500, padding: '3px 10px', borderRadius: '20px' }}>
                {totalCount} activities
              </span>
            )}
            {dataDateBadge && (
              <span style={{ background: 'var(--tint)', color: 'var(--accent)', fontSize: '11px', fontWeight: 500, padding: '3px 10px', borderRadius: '20px' }}>
                Data date {dataDateBadge}
              </span>
            )}
            {completionBadgeDate && (
              <span style={{ background: 'var(--watch-bg)', color: 'var(--watch-text)', fontSize: '11px', fontWeight: 500, padding: '3px 10px', borderRadius: '20px' }}>
                Completion {completionBadgeDate}
              </span>
            )}
          </div>
        </div>

        {/* ── 5 metric cards — 2 per row on phones, 3 on iPad portrait, 5 from lg ── */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 mb-3">
          {metrics.map((m, i) => (
            <div key={i} style={{ ...card, padding: '12px 14px', minWidth: 0 }}>
              <div style={{ fontSize: '11px', color: 'var(--muted)', marginBottom: '4px' }}>{m.label}</div>
              <div className="kpi-num" style={{ color: m.color }}>
                {loading && i > 1 ? '—' : m.value}
              </div>
              <div style={{ fontSize: '11px', color: 'var(--faint)', marginTop: '2px' }}>{m.sub}</div>
            </div>
          ))}
        </div>

        {/* ── Error state ── */}
        {error && (
          <div style={{ background: 'var(--crit-bg)', border: '0.5px solid #A32D2D40', borderRadius: '8px', padding: '12px', marginBottom: '12px', fontSize: '12px', color: 'var(--crit-text)' }}>
            {error}
          </div>
        )}

        {/* ── Loading placeholder for charts ── */}
        {loading && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mb-3">
            {[0, 1, 2].map(i => (
              <div key={i} style={{ ...card, height: '220px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div style={{ fontSize: '12px', color: 'var(--faint)' }}>Loading…</div>
              </div>
            ))}
          </div>
        )}

        {/* ── Charts + bottom row — only when data loaded ── */}
        {!loading && activities.length > 0 && (
          <>
            {/* Charts: stacked on phones, 2-up on iPad portrait (timeline spans the row),
                3-up from lg. ResponsiveContainer resizes each chart to its cell. */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mb-3">

              {/* Chart 1: Float distribution */}
              <div style={{ ...card, padding: '14px', minWidth: 0 }}>
                <div className="card-title" style={{ marginBottom: '10px' }}>Float distribution</div>
                <ResponsiveContainer width="100%" height={160}>
                  <BarChart data={floatData} margin={{ top: 5, right: 5, bottom: 5, left: -20 }}>
                    <XAxis dataKey="name" tick={{ fontSize: 10 }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 10 }} axisLine={false} tickLine={false} allowDecimals={false} />
                    <Tooltip
                      contentStyle={{ fontSize: 11, borderRadius: 6, border: '1px solid var(--border)' }}
                      formatter={(v: any) => [`${v} activities`]}
                    />
                    <Bar dataKey="value" radius={[3, 3, 0, 0]}>
                      {floatData.map((entry, index) => (
                        <Cell key={index} fill={entry.fill} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* Chart 2: Float trend */}
              <div style={{ ...card, padding: '14px', minWidth: 0 }}>
                <div className="card-title" style={{ marginBottom: '4px' }}>Float trend</div>
                <div style={{ display: 'flex', gap: '12px', marginBottom: '8px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '9px', color: 'var(--muted)' }}>
                    <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: 'var(--crit-text)' }} />
                    XER upload
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '9px', color: 'var(--muted)' }}>
                    <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: 'white', border: '1.5px solid var(--crit-text)' }} />
                    In-app change
                  </div>
                </div>
                {trendData.length < 2 ? (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '120px', color: 'var(--faint)', fontSize: '11px', textAlign: 'center', padding: '0 16px' }}>
                    Float trend will appear after 2+ CPM runs
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height={140}>
                    <LineChart data={trendData} margin={{ top: 5, right: 5, bottom: 5, left: -20 }}>
                      <XAxis dataKey="date" tick={{ fontSize: 9 }} axisLine={false} tickLine={false} />
                      <YAxis tick={{ fontSize: 9 }} axisLine={false} tickLine={false} />
                      <Tooltip content={<TrendTooltip />} />
                      <ReferenceLine y={0} stroke="var(--crit-text)" strokeDasharray="3 3" strokeOpacity={0.5} />
                      <Line
                        type="monotone"
                        dataKey="float"
                        stroke="var(--crit-text)"
                        strokeWidth={1.5}
                        dot={<TrendDot />}
                        activeDot={false}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                )}
              </div>

              {/* Chart 3: Critical path Gantt — CSS bars */}
              <div className="md:col-span-2 lg:col-span-1" style={{ ...card, padding: '14px', minWidth: 0 }}>
                <div className="card-title" style={{ marginBottom: '10px' }}>Critical path timeline</div>
                {criticalActivities.length === 0 ? (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '140px', color: 'var(--faint)', fontSize: '12px' }}>
                    No critical activities
                  </div>
                ) : (
                  <div style={{ padding: '2px 0' }}>
                    {criticalActivities.map(a => (
                      <div key={a.activity_id} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '3px 0', borderBottom: '0.5px solid var(--border)' }}>
                        <div style={{ fontSize: '9px', fontWeight: 500, width: '46px', flexShrink: 0, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {a.activity_id}
                        </div>
                        <div style={{ flex: 1, height: '13px', background: 'var(--table-head-bg)', borderRadius: '3px', position: 'relative', overflow: 'hidden' }}>
                          <div style={{
                            position: 'absolute',
                            left: `${ganttLeft(a.early_start)}%`,
                            width: `${ganttWidth(a.early_start, a.early_finish)}%`,
                            height: '100%',
                            background: Number(a.float_days) < 0 ? 'var(--crit-text)' : 'var(--watch-border)',
                            borderRadius: '3px',
                          }} />
                        </div>
                      </div>
                    ))}
                    <div style={{ display: 'flex', gap: 12, marginTop: 8 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 9, color: 'var(--muted)' }}>
                        <div style={{ width: 10, height: 6, background: 'var(--crit-text)', borderRadius: 2 }} /> Critical (negative float)
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 9, color: 'var(--muted)' }}>
                        <div style={{ width: 10, height: 6, background: 'var(--watch-border)', borderRadius: 2 }} /> At risk
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Bottom row: critical table (2/3) + quick actions (1/3) from lg; stacked below */}
            <div className="grid grid-cols-1 lg:grid-cols-[2fr_1fr] gap-3">

              {/* Critical activity table. On phones the Status column is dropped (every
                  row here is "Critical" by definition) so ID + Name + Float + Finish fit
                  without any sideways scroll. */}
              <div id="most-critical-activities" style={{ ...card, padding: '14px', minWidth: 0 }}>
                <div className="card-title" style={{ marginBottom: '10px' }}>
                  Most critical activities
                  <span style={{ fontSize: '11px', fontWeight: 400, color: 'var(--muted)', marginLeft: '6px' }}>sorted by float</span>
                </div>

                {/* Table header */}
                <div className="grid grid-cols-[58px_1fr_40px_76px] sm:grid-cols-[70px_1fr_52px_82px_70px] gap-2" style={{ padding: '6px 8px', background: 'var(--table-head-bg)', borderRadius: '6px', marginBottom: '4px' }}>
                  {['Activity', 'Name', 'Float', 'Finish', 'Status'].map(h => (
                    <div key={h} className={h === 'Status' ? 'hidden sm:block' : ''} style={{ fontSize: '10px', color: 'var(--faint)', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.03em' }}>{h}</div>
                  ))}
                </div>

                {/* Table rows */}
                {criticalCount === 0 ? (
                  <div style={{ padding: '20px', textAlign: 'center', color: 'var(--faint)', fontSize: '12px' }}>No critical activities found</div>
                ) : (
                  activities
                    .filter(a => a.is_critical)
                    .sort((a, b) => Number(a.float_days) - Number(b.float_days))
                    .slice(0, 8)
                    .map(a => (
                      <div key={a.activity_id} className="grid grid-cols-[58px_1fr_40px_76px] sm:grid-cols-[70px_1fr_52px_82px_70px] gap-2 items-center" style={{ padding: '6px 8px', borderBottom: '0.5px solid var(--border)' }}>
                        <div style={{ fontSize: '11px', fontWeight: 500, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.activity_id}</div>
                        <div style={{ fontSize: '11px', color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.activity_name}</div>
                        <div style={{ fontSize: '11px', fontWeight: 500, color: Number(a.float_days) < 0 ? 'var(--crit-text)' : 'var(--watch-text)' }}>{a.float_days}d</div>
                        <div style={{ fontSize: '10px', color: 'var(--muted)', whiteSpace: 'nowrap' }}>
                          {a.early_finish ? formatDate(a.early_finish) : '—'}
                        </div>
                        <div className="hidden sm:block">
                          <span style={{ background: 'var(--crit-bg)', color: 'var(--crit-text)', fontSize: '10px', padding: '2px 6px', borderRadius: '20px', fontWeight: 500, whiteSpace: 'nowrap' }}>
                            Critical
                          </span>
                        </div>
                      </div>
                    ))
                )}

                {criticalCount > 8 && (
                  <div style={{ paddingTop: '8px', paddingLeft: '8px' }}>
                    <button
                      onClick={() => navigate(`/project/${project.id}/lookahead?tab=all&critical=1`)}
                      className="tap"
                      style={{ fontSize: '12px', color: 'var(--accent)', fontWeight: 500, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                    >
                      View all {criticalCount} critical activities →
                    </button>
                  </div>
                )}
              </div>

              {/* Quick actions — 2×2 grid */}
              <div style={{ ...card, padding: '14px' }}>
                <div className="card-title" style={{ marginBottom: '10px' }}>Quick actions</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                  {quickActions.map((action, i) => (
                    <div
                      key={i}
                      onClick={action.onClick}
                      style={{ border: '0.5px solid var(--border)', borderRadius: '10px', padding: '12px', cursor: 'pointer', transition: 'border-color 0.15s' }}
                      onMouseEnter={e => { (e.currentTarget as HTMLDivElement).style.borderColor = action.color; }}
                      onMouseLeave={e => { (e.currentTarget as HTMLDivElement).style.borderColor = 'var(--border)'; }}
                    >
                      <div style={{ fontSize: '20px', marginBottom: '6px' }}>{action.emoji}</div>
                      <div className="font-display" style={{ fontSize: '14px', fontWeight: 700, color: 'var(--ink)', marginBottom: '2px' }}>{action.title}</div>
                      <div style={{ fontSize: '11px', color: 'var(--muted)' }}>{action.subtitle}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </>
        )}

        {/* ── Empty state ── */}
        {!loading && !error && activities.length === 0 && (
          <div style={{ ...card, padding: '48px 24px', textAlign: 'center' }}>
            <div style={{ fontSize: '13px', color: 'var(--faint)', marginBottom: '16px' }}>
              No schedule data yet. Upload a schedule to see analytics.
            </div>
            <button
              onClick={() => navigate(`/project/${project.id}/upload`)}
              style={{ background: 'var(--accent)', color: 'white', padding: '10px 20px', borderRadius: '8px', border: 'none', fontSize: '13px', fontWeight: 500, cursor: 'pointer' }}
            >
              Upload schedule
            </button>
          </div>
        )}

      
    </PageShell>
  );
};

export default Dashboard;
